/**
 * Profile Generators for Flux PhD simulations.
 *
 * Generates minute-by-minute load profiles for solar, EV, and AC loads.
 * Used by SimulationConfig and the usePhdSimulation hook.
 */

/**
 * Generate minute-by-minute solar profile (Gaussian bell curve peaking at noon).
 */
export const generateSolarProfile = (minutes, peakKw) => {
    return Array.from({ length: minutes }, (_, i) => {
        const minuteOfDay = i % 1440;
        const hour = minuteOfDay / 60;

        if (hour >= 6 && hour <= 18) {
            const peak = 12;
            const spread = 3;
            const factor = Math.exp(-Math.pow(hour - peak, 2) / (2 * spread * spread));
            return peakKw * factor;
        }
        return 0;
    });
};

/**
 * Generate minute-by-minute EV load profile based on profile type.
 * Supported types: commercial, fleet_depot, retail, residential.
 */
export const generateEvLoadProfile = (minutes, chargerCount, chargerKw, profileType) => {
    const maxLoad = chargerCount * chargerKw;
    return Array.from({ length: minutes }, (_, i) => {
        const minuteOfDay = i % 1440;
        const hour = minuteOfDay / 60;

        let utilization = 0.1; // baseline

        switch (profileType) {
            case 'fleet_depot':
                if (hour >= 22 || hour <= 5) {
                    utilization = 0.85;
                } else if (hour >= 6 && hour <= 8) {
                    utilization = 0.3;
                } else {
                    utilization = 0.1;
                }
                break;

            case 'retail':
                if (hour >= 10 && hour <= 14) {
                    utilization = 0.4;
                } else if (hour >= 17 && hour <= 21) {
                    utilization = 0.7;
                } else if (hour >= 14 && hour <= 17) {
                    utilization = 0.3;
                }
                break;

            case 'residential':
                if (hour >= 6 && hour <= 8) {
                    utilization = 0.5;
                } else if (hour >= 18 && hour <= 22) {
                    utilization = 0.8;
                } else if (hour >= 22 || hour <= 5) {
                    utilization = 0.6;
                }
                break;

            case 'commercial':
            default:
                if (hour >= 7 && hour <= 9) {
                    utilization = 0.4 + 0.2 * Math.sin((hour - 7) * Math.PI / 2);
                } else if (hour >= 9 && hour <= 17) {
                    utilization = 0.6;
                } else if (hour >= 17 && hour <= 19) {
                    utilization = 0.4;
                }
                break;
        }

        const noise = (Math.random() - 0.5) * 0.05;
        return Math.max(0, maxLoad * (utilization + noise));
    });
};

/**
 * Generate minute-by-minute AC load profile (building loads).
 */
export const generateAcLoadProfile = (minutes, baseKw = 20) => {
    return Array.from({ length: minutes }, (_, i) => {
        const minuteOfDay = i % 1440;
        const hour = minuteOfDay / 60;

        let factor = 0.3; // overnight baseline

        if (hour >= 6 && hour <= 8) {
            factor = 0.3 + 0.5 * ((hour - 6) / 2);
        } else if (hour >= 8 && hour <= 18) {
            factor = 0.8 + 0.2 * Math.sin((hour - 8) * Math.PI / 10);
        } else if (hour >= 18 && hour <= 22) {
            factor = 0.8 - 0.5 * ((hour - 18) / 4);
        }

        return baseKw * factor;
    });
};
